import { ChangeDetectorRef, Directive, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { ConditionalDirective } from '@app/shared/abstracts/conditional.directive';
import { NgIfContext } from '@angular/common';
import { filter, takeUntil, catchError } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { CompanyLimitationService } from '@app/shared/services/company-limitation.service';
import { EnvService } from '@app/env.service';

@Directive({
  selector: '[tcCompanyLimitation]',
})
export class CompanyLimitationDirective extends ConditionalDirective implements OnDestroy {
  private destroy$ = new Subject<void>();
  private currentAllowedIds: string[] = [];

  @Input()
  set tcCompanyLimitation(stringifiedList: string) {
    if (!stringifiedList || stringifiedList.trim() === '') {
      this.currentAllowedIds = [];
      this.checkCompanyLimitation();

      return;
    }

    const list = stringifiedList
      .split(',')
      .map(id => {
        return id.trim();
      })
      .filter(id => {
        return id;
      });

    this.currentAllowedIds = list;
    this.checkCompanyLimitation();
  }

  constructor(
    private companyLimitService: CompanyLimitationService,
    private envService: EnvService,
    private cdr: ChangeDetectorRef,
    viewContainer: ViewContainerRef,
    templateRef: TemplateRef<NgIfContext>,
  ) {
    super(viewContainer, templateRef);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkCompanyLimitation(): void {
    // Check if mock modules are enabled first
    if (this.envService.showMockModules) {
      this.updateViewWithLogging(true, 'mock_mode');

      return;
    }

    // Single subscription with proper cleanup and error handling
    this.companyLimitService.connectionIds$
      .pipe(
        takeUntil(this.destroy$),
        filter(connectionIds => {
          const isValid = Array.isArray(connectionIds);

          return isValid;
        }),
        catchError(error => {
          // Graceful fallback: hide element on error
          this.updateViewWithLogging(false, 'error_fallback');

          return of([]);
        }),
      )
      .subscribe({
        next: connectionIds => {
          const allowed = this.isCompanyAllowed(connectionIds, this.currentAllowedIds);
          const reason = allowed ? 'connection_match' : 'no_match';

          this.updateViewWithLogging(allowed, reason);
        },
        error: error => {
          // Fallback to hiding element on subscription error
          this.updateViewWithLogging(false, 'subscription_error');
        },
      });
  }

  private isCompanyAllowed(connectionIds: string[], allowedIds: string[]): boolean {
    if (!connectionIds || !Array.isArray(connectionIds)) {
      return false;
    }

    if (!allowedIds || allowedIds.length === 0) {
      return false;
    }

    const hasMatch = connectionIds.some(id => {
      return allowedIds.includes(id);
    });

    return hasMatch;
  }

  private updateViewWithLogging(show: boolean, reason: string): void {
    try {
      this.updateView(show);

      // Ensure change detection runs
      if (this.cdr) {
        this.cdr.markForCheck();
      }
    } catch (error) {
      // Try to recover by hiding the element
      try {
        this.updateView(false);
      } catch (recoveryError) {
        // Silent fallback - element will remain hidden
      }
    }
  }
}
